/**
 * MonitoredGrid - Grid display of monitored sessions with drag-and-drop
 *
 * Features:
 * - Shows only monitored sessions
 * - Drag-and-drop reordering
 * - Empty state guidance
 */

import { useMemo } from 'react'
import { Title, Text, Group, Badge, Grid, Card } from '@mantine/core'
import { IconBell } from '@tabler/icons-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy
} from '@dnd-kit/sortable'
import { SortableSessionCard } from './SortableSessionCard'
import type { SessionInfo, LastChangeTimeMap } from '@session-tools/monitor-ui-core'

interface MonitoredGridProps {
  sessions: SessionInfo[]
  monitoredSessions: string[]
  orderedMonitoredSessions: SessionInfo[]
  mutedSessions: string[]
  poolSessions: string[]
  lastChangeTimeMap: LastChangeTimeMap
  onReorder: (activeId: string, overId: string) => void
  onToggleMute: (name: string) => void
  onUnmonitor: (name: string) => void
}

export function MonitoredGrid({
  sessions,
  monitoredSessions,
  orderedMonitoredSessions,
  mutedSessions,
  poolSessions,
  lastChangeTimeMap,
  onReorder,
  onToggleMute,
  onUnmonitor
}: MonitoredGridProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      onReorder(active.id as string, over.id as string)
    }
  }

  // Status counts for monitored sessions only
  const statusCounts = useMemo(() => {
    const monitored = sessions.filter(s => monitoredSessions.includes(s.name))
    return {
      total: monitored.length,
      waiting: monitored.filter(s => s.status === 'waiting').length,
      active: monitored.filter(s => s.status === 'active').length,
    }
  }, [sessions, monitoredSessions])

  if (orderedMonitoredSessions.length === 0) {
    return (
      <Card withBorder p="xl" ta="center">
        <IconBell size={48} stroke={1} color="var(--mantine-color-dimmed)" />
        <Text c="dimmed" size="lg" mt="md">
          No monitored sessions
        </Text>
        <Text c="dimmed" size="sm" mt="xs">
          Click "Monitor" on a session above to receive sound alerts when it needs attention
        </Text>
      </Card>
    )
  }

  return (
    <>
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <IconBell size={24} />
          <Title order={3}>Monitored Sessions</Title>
        </Group>
        <Group gap="xs">
          <Text size="xs" c="dimmed">Drag to reorder</Text>
          {statusCounts.waiting > 0 && (
            <Badge size="sm" color="yellow" variant="filled">
              {statusCounts.waiting} waiting
            </Badge>
          )}
          {statusCounts.active > 0 && (
            <Badge size="sm" color="blue" variant="light">
              {statusCounts.active} active
            </Badge>
          )}
          <Badge size="sm" color="violet" variant="filled">
            {statusCounts.total} monitored
          </Badge>
        </Group>
      </Group>

      {/* Grid with DnD */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={orderedMonitoredSessions.map(s => s.name)}
          strategy={rectSortingStrategy}
        >
          <Grid>
            {orderedMonitoredSessions.map((session) => (
              <Grid.Col key={session.name} span={{ base: 12, sm: 6, lg: 4 }}>
                <SortableSessionCard
                  id={session.name}
                  session={session}
                  isMuted={mutedSessions.includes(session.name)}
                  isInPool={poolSessions.includes(session.name)}
                  lastChangeTime={lastChangeTimeMap.get(session.name)}
                  onToggleMute={() => onToggleMute(session.name)}
                  onUnmonitor={() => onUnmonitor(session.name)}
                />
              </Grid.Col>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
    </>
  )
}
